import {
  Timeline,
  TimelineConnector,
  TimelineContent,
  TimelineDot,
  TimelineItem,
  TimelineSeparator,
} from "@mui/lab";
import createStyles from "@mui/styles/createStyles";
import makeStyles from "@mui/styles/makeStyles";
import withStyles from "@mui/styles/withStyles";
import { FormSection } from "components/FormControls/FormSection";
import { ReactComponent as Gift01Icon } from "components/Icons/gift-01.svg";
import { ReactComponent as ProfileIcon } from "components/Icons/profileIcon.svg";
import { ReactComponent as Star01Icon } from "components/Icons/star-01.svg";
import { SimulationEffect } from "components/Layout/Campaign/CampaignSimulation/types";
import { OLTheme } from "contexts/Theme/ThemeProvider";
import { useMessage } from "hooks/intl";
import { CampaignEffectType } from "types/campaigns";

const simulationStyles = makeStyles((theme: OLTheme) =>
  createStyles({
    connector: {
      background: theme.gray200,
    },
    header: {
      color: theme.primary800,
      fontWeight: 400,
      fontSize: 16,
      lineHeight: theme.spacing(10),
      margin: 0,
    },
    textType: {
      color: theme.gray500,
      fontSize: 11,
      lineHeight: theme.spacing(8),
      textTransform: "uppercase",
    },
    textInfo: {
      color: theme.gray400,
      fontSize: 13,
      lineHeight: theme.spacing(8),
      marginTop: theme.spacing(1),
    },
    noDataWrapper: {
      display: "flex",
      justifyContent: "center",
    },
    noData: {
      fontWeight: 400,
      fontSize: 14,
      color: theme.gray500,
    },
    itemHeader: {
      display: "flex",
      alignItems: "center",
    },
    iconWrapper: {
      borderRadius: "50%",
      width: theme.spacing(14),
      height: theme.spacing(14),
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      backgroundColor: theme.gray100,
      marginRight: theme.spacing(4),

      "& svg": {
        color: theme.gray500,
        width: "16px",
        height: "16px",
        stroke: theme.gray500,
      },
    },
    content: {
      padding: theme.spacing(1, 8, 3, 8),
    },
  }),
);

const CustomTimelineItem = withStyles({
  missingOppositeContent: { "&::before": { display: "none" } },
})(TimelineItem);

const renderHeader = (type?: CampaignEffectType) => {
  switch (type) {
    case "give_points":
    case "deduct_unit":
      return { icon: <Star01Icon />, title: "newPointsTransfer" };
    case "assign_member_custom_attribute":
      return { icon: <ProfileIcon />, title: "memberCustomAttributeWasSet" };
    case "remove_member_custom_attribute":
      return {
        icon: <ProfileIcon />,
        title: "memberCustomAttributeWasRemoved",
      };
    case "give_reward":
      return { icon: <Gift01Icon />, title: "newReward" };
    default:
      return { icon: null, title: "-" };
  }
};

export function SimulatedResultsTimeline({
  items,
}: {
  items?: SimulationEffect[];
}) {
  const classes = simulationStyles();
  const { message } = useMessage();
  const areResults = items && items.length > 0;

  return (
    <FormSection title={message({ id: "simulatedResults" })} noPadding>
      <Timeline>
        {areResults ? (
          items?.map((item: SimulationEffect, index: number) => {
            const isLastItem = items.length - 1 === index;
            return (
              <CustomTimelineItem key={index}>
                <TimelineSeparator>
                  <TimelineDot variant="outlined" color="primary" />
                  {!isLastItem && (
                    <TimelineConnector className={classes.connector} />
                  )}
                </TimelineSeparator>
                <TimelineContent className={classes.content}>
                  <div className={classes.itemHeader}>
                    <span className={classes.iconWrapper}>
                      {renderHeader(item.type).icon}
                    </span>
                    <div className={classes.textType}>
                      {message({
                        id: renderHeader(item.type).title,
                      })}
                    </div>
                  </div>

                  <div className={classes.header}>
                    {message(
                      {
                        id:
                          item.type === "give_points"
                            ? "dynamicPointsEarned"
                            : item.type === "deduct_unit"
                              ? "dynamicPointsSpent"
                              : item.type === "give_reward"
                                ? "dynamicRewardRedeemed"
                                : [
                                      "assign_member_custom_attribute",
                                      "remove_member_custom_attribute",
                                    ].includes(item.type!)
                                  ? "customAttributeKey"
                                  : "-",
                      },
                      {
                        value: ["give_points", "deduct_unit"].includes(
                          item.type!,
                        )
                          ? item.points
                          : item.type === "give_reward"
                            ? item.reward?.name
                            : [
                                  "assign_member_custom_attribute",
                                  "remove_member_custom_attribute",
                                ].includes(item.type!)
                              ? item.customAttributeKey
                              : "-",
                      },
                    )}
                    {item?.target?.target === "self" &&
                      ` - ${message({ id: "member" })}`}
                    {item?.target?.target === "referrer" &&
                      ` - ${message({ id: "referrer" })}`}
                  </div>
                  {item.type === "assign_member_custom_attribute" &&
                    item.customAttributeValue && (
                      <div className={classes.header}>
                        {message(
                          { id: "customAttributeValue" },
                          { value: item.customAttributeValue },
                        )}
                      </div>
                    )}
                  <div className={classes.textInfo}>{item.couponValue}</div>
                  <div className={classes.textInfo}>
                    {item.reward?.couponValue}
                  </div>
                </TimelineContent>
              </CustomTimelineItem>
            );
          })
        ) : (
          <div className={classes.noDataWrapper}>
            <span className={classes.noData}>{message({ id: "noData" })}</span>
          </div>
        )}
      </Timeline>
    </FormSection>
  );
}
